// @flow
import styled, { keyframes, css } from 'styled-components';
import * as React from 'react';
import Link from 'components/Link';
import { H1, HeadingSmall, Body } from 'components/Text';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Img from 'components/Image';
import { gutterWidth } from 'theme';

const aFloat = () => keyframes`
  0% {
    transform: translate(0,  0px) rotateZ(0);
  }

  50% {
    transform: translate(0, -10px) rotateZ(0);
  }

  100% {
    transform: translate(0, -0px) rotateZ(0);
  }
`;

const ImageContainer = styled(Box)`
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${aFloat()} 3s infinite cubic-bezier(0.37, 0, 0.63, 1);
    `}
`;

export default ({
  searchText,
  searchUrl,
  homepageUrl,
  textOnly
}: {
  searchText?: string,
  searchUrl?: string,
  homepageUrl?: string,
  textOnly?: boolean
}) => (
  <Box display={!textOnly && ['block', null, 'flex']} alignItems="center">
    <Box
      width={!textOnly && [1, null, 1 / 2]}
      pr={!textOnly && [0, null, gutterWidth]}
    >
      <HeadingSmall color="battleshipGrey">
        {'404: '}
        {getText('Page Not Found')}
      </HeadingSmall>

      <H1>
        {/* TRANSLATORS: 404 page title */ getText(`Well, this isn't ideal...`)}
      </H1>

      <Body>
        {/* TRANSLATORS: 404 page copy */ getText(
          `Sorry, we couldn't find the page you were looking for.
          The link you followed may be broken, or the page may have been removed.
          Try the following options:`
        )}
      </Body>

      <Box mt={(gutterWidth * 3) / 4}>
        <Link href={searchUrl || `/jobs`}>
          <Box display="flex" alignItems="center">
            <Box transform="translateY(3px)">
              <Img
                src="/static/images/magifying-glass.png"
                alt="magnifying glass"
                width={24}
              />
            </Box>
            <Box ml={24}>
              {searchText ||
                (searchUrl
                  ? getText('Search the site')
                  : getText('Search jobs'))}
            </Box>
          </Box>
        </Link>

        <Divider light my={gutterWidth / 2} />

        <Link href={homepageUrl || '/'}>
          <Box display="flex" alignItems="center">
            <Box transform="translateY(3px)">
              <Img
                src="/static/images/logo-mark.png"
                alt="magnifying glass"
                width={24}
                webp
              />
            </Box>
            <Box ml={24}>{getText('Go to the homepage')}</Box>
          </Box>
        </Link>
      </Box>
    </Box>

    {!textOnly && (
      <Box
        position="relative"
        width={[1, null, 1 / 2]}
        transform={['none', null, 'translate(5%, 24px) scale(1.1)']}
        mt={[gutterWidth, null, 0]}
      >
        <Box pb="80%" />
        {Array(2)
          .fill(0)
          .map((_, index) => (
            <ImageContainer
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              position="absolute"
              top={0}
              left={0}
              width={1}
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              $animate={index === 0}
            >
              <Img
                src={`/static/images/not-found-layer-${index}.png`}
                layout="responsive"
                width="100%"
                alt="Illustration"
                webp
              />
            </ImageContainer>
          ))}
      </Box>
    )}
  </Box>
);
